// Sales-floor capability helpers (Fulfillment Slice 3).
//
// Per docs/planning/sales_fulfillment_bucket.md §3, the Fulfillment
// bucket is a baseline (no-entitlement) bucket whose visibility is
// gated by the per-org capability `organizations.has_sales_floor`
// (default true; flipped off for plant-only manufacturer orgs). This
// helper centralizes:
//
//   - `coerceHasSalesFloor` — defensive boolean coercion at the load
//     boundary (auth state from Supabase, or the admin-toggle RPC
//     return). Treats `null` / `undefined` / non-boolean as `false`
//     (fail closed).
//   - `FULFILLMENT_BUCKET_MODES` — the three bucket-gated mode ids,
//     consumed by both the access predicate and the daily-checklist
//     nag so the gate stays single-source-of-truth.
//   - `requiresSalesFloor` — pure predicate that maps a mode to "this
//     mode requires has_sales_floor=true to be visible".
//
// Stays pure / no-React so the same code can drive the server-side
// `requireFulfillmentApiAuth` gate (which imports from `src/lib/...` via
// the .js suffix per the existing fulfillment server-side pattern).

export const FULFILLMENT_BUCKET_MODES = [
  "fulfillmentInventory",
  "fulfillmentOrders",
  "fulfillmentIntake",
  "fulfillmentForms",
  "fulfillmentEntry",
] as const;

export type FulfillmentBucketMode = (typeof FULFILLMENT_BUCKET_MODES)[number];

export function isFulfillmentBucketMode(mode: string): mode is FulfillmentBucketMode {
  return (FULFILLMENT_BUCKET_MODES as readonly string[]).includes(mode);
}

/** Defensive boolean coercion. Anything other than literal `true` returns false (fail closed). */
export function coerceHasSalesFloor(raw: unknown): boolean {
  return raw === true;
}

/** Returns true iff this mode is gated by the has_sales_floor capability. */
export function requiresSalesFloor(mode: string): boolean {
  return isFulfillmentBucketMode(mode);
}
